// src/modules/app-views/customers/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import CustomersInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import customersInitActionButton from 'modules/app-views/customers/pages/Init/actions/actionButton';
import customersInitMenuButton from 'modules/app-views/customers/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CustomersParametersModal from 'modules/app-views/customers/pages/Init/modals/CustomersParametersModal';
import seedCustomersParameters from 'modules/app-views/customers/seed';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CustomersDocument from 'modules/app-views/customers/pages/Document';
import customersDocumentActionButton from 'modules/app-views/customers/pages/Document/actions/actionButton';
import customersDocumentDropdownMenu from 'modules/app-views/customers/pages/Document/actions/dropdownMenu';
import customersDocumentMenuButton from 'modules/app-views/customers/pages/Document/actions/menuButton';
import customersDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  documentCodeItem,
  createdAtItem,
  updatedAtItem,
  emailItem,
  phoneItem,
  locationItem,
  websiteItem,
  genderSegmentItem,
  cpfCnpjItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import CustomersToolbarLeft from './pages/Init/components/CustomersToolbarLeft';
import CustomersPreviewBody from './components/CustomersPreviewBody';
import CustomersPreviewHeader from './components/CustomersPreviewHeader';
import CustomersPreviewActions from './components/CustomersPreviewActions';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'customers';

// Entity configuration - single source of truth for entityType and entityProfile
// - entityType: ['company'] = only legal entities (legal persons)
// - entityType: ['person'] = only individuals (natural persons)
// - entityType: ['company', 'person'] = both (hybrid mode)
const ENTITY_CONFIG = {
  entityProfile: ['customer'],
  entityType: ['person', 'company'],
};

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: CustomersParametersModal,
  parametersSeed: seedCustomersParameters,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY CONFIGURATION
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: 'customer_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list', // 'page' | 'list' | 'document'
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <CustomersInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    // Sort structure: array of { field, order, index }
    // - field: string - Field path (e.g., 'name', 'cnpj.value', '_metadata.audit.createdAt')
    // - order: 'asc' | 'desc' - Sort direction
    // - index: number - Sort priority (1 = highest, sorted first)
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
        // { field: 'name', order: 'asc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: ENTITY_CONFIG,
      drawer: { folder: 'inbox', dateField: 'createdAt' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'view', 'archive', 'remove'],
      hiddenColumns: ['line', 'cpfCnpj', 'name', 'fullName', 'fullNameWithAvatar', 'updatedAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default', // 'default' | 'card'
      actionsInContent: true,
      leftContent: <CustomersToolbarLeft />,
      actions: ['refresh', customersInitActionButton(), customersInitMenuButton()],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // PREVIEW DRAWER
    // ───────────────────────────────────────────────────────────────────────
    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'customer',
        header: props => <CustomersPreviewHeader {...props} />,
        body: props => <CustomersPreviewBody {...props} />,
        actions: props => <CustomersPreviewActions {...props} />,
        footer: null,
      },
    },
  },

  document: {
    highSecurity: false, // Verify userId in URL params matches storedUser._id

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <CustomersDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.URL,
      params: null,
      query: {
        // Filter uses ENTITY_CONFIG for consistency with init page
        filter: JSON.stringify(ENTITY_CONFIG),
      },

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: customersDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        customersDocumentActionButton(),
        customersDocumentMenuButton(),
        customersDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'
      //
      avatar: {
        visible: true,
        placeholder: 'logo',
      },
      //
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_customer" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),
        column2: props => (
          <CardHeaderColumn {...props} items={[emailItem, phoneItem, locationItem]} />
        ),
        column3: props => (
          <CardHeaderColumn {...props} items={[cpfCnpjItem, genderSegmentItem, websiteItem]} />
        ),
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // CUSTOM COMPONENTS
    // ───────────────────────────────────────────────────────────────────────
    // components: {},
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
